import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Eye, Cpu, Key, Shield, Save, Check, Loader2, Activity } from 'lucide-react';
import { getSettings, updateSettings, getVisionModels, getThinkingModels, getRawKeys } from '../lib/api';
import { toast } from 'sonner';
import AdminPanel from '../components/settings/AdminPanel';

export default function Settings() {
  const [visionModels, setVisionModels] = useState([]);
  const [thinkingModels, setThinkingModels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keysConfigured, setKeysConfigured] = useState({ openrouter: false, google: false });

  // Local form state
  const [apiKeys, setApiKeys] = useState({ openrouter_key: '', google_ai_key: '' });
  const [selectedVision, setSelectedVision] = useState('gemini-3-flash');
  const [selectedThinking, setSelectedThinking] = useState('minimax-m2-5');
  const [visionProvider, setVisionProvider] = useState('google');
  const [thinkingProvider, setThinkingProvider] = useState('openrouter');
  const [ruleSettings, setRuleSettings] = useState({
    allow_diagonals: false,
    allow_wave4_overlap: false,
    zigzag_sensitivity: 5.0,
    min_wave_size_pct: 1.0,
    fib_tolerance: 0.05,
    interpretation_strictness: 0.5,
    require_ai_assist: true,
    confidence_mode_enabled: false,
    confidence_mode_limit: 5,
    confidence_mode_min_score: 75,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, vm, tm, rk] = await Promise.all([
        getSettings(), getVisionModels(), getThinkingModels(), getRawKeys()
      ]);
      setSettings(s);
      setVisionModels(vm);
      setThinkingModels(tm);
      setKeysConfigured({ openrouter: rk.openrouter_configured, google: rk.google_ai_configured });

      if (s.model_config_setting) {
        setSelectedVision(s.model_config_setting.vision_model || 'gemini-3-flash');
        setSelectedThinking(s.model_config_setting.thinking_model || 'minimax-m2-5');
        setVisionProvider(s.model_config_setting.vision_provider || 'google');
        setThinkingProvider(s.model_config_setting.thinking_provider || 'openrouter');
      }
      if (s.rule_settings) {
        setRuleSettings(prev => ({ ...prev, ...s.rule_settings }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        model_config_setting: {
          vision_model: selectedVision,
          thinking_model: selectedThinking,
          vision_provider: visionProvider,
          thinking_provider: thinkingProvider,
        },
        rule_settings: ruleSettings,
      };
      // Only include API keys if user entered new ones
      if (apiKeys.openrouter_key || apiKeys.google_ai_key) {
        payload.api_keys = {};
        if (apiKeys.openrouter_key) payload.api_keys.openrouter_key = apiKeys.openrouter_key;
        if (apiKeys.google_ai_key) payload.api_keys.google_ai_key = apiKeys.google_ai_key;
      }
      await updateSettings(payload);
      setApiKeys({ openrouter_key: '', google_ai_key: '' });
      toast.success('Settings saved');
      load();
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const RatingBar = ({ value, max = 10, color }) => (
    <div className="wk-rating-bar w-full">
      <div className="wk-rating-fill" style={{ width: `${(value / max) * 100}%`, backgroundColor: color }} />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#f5b400]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl animate-fade-in" data-testid="settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Settings</h1>
          <p className="text-sm text-[#a1a1aa] mt-1">Configure models, API keys, and analysis rules</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm font-medium active:scale-95 transition-transform"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <Tabs defaultValue="models" className="space-y-4">
        <TabsList className="bg-[#14161a] border border-white/5 rounded-sm p-1">
          <TabsTrigger value="models" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-models">
            <Eye className="w-3 h-3 mr-1.5" /> Models
          </TabsTrigger>
          <TabsTrigger value="keys" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-keys">
            <Key className="w-3 h-3 mr-1.5" /> API Keys
          </TabsTrigger>
          <TabsTrigger value="rules" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-rules">
            <Shield className="w-3 h-3 mr-1.5" /> Rules
          </TabsTrigger>
          <TabsTrigger value="admin" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-admin">
            <Activity className="w-3 h-3 mr-1.5" /> Admin
          </TabsTrigger>
        </TabsList>

        {/* MODELS TAB */}
        <TabsContent value="models" className="space-y-6">
          {/* Vision Models */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#f5b400]" /> Vision Models
            </h3>
            <p className="text-xs text-[#a1a1aa] mb-4">Select the AI model for chart image analysis</p>
            <ScrollArea className="h-[400px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-4" data-testid="vision-models-grid">
                {visionModels.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`vision-model-${m.id}`}
                    onClick={() => {
                      setSelectedVision(m.id);
                      setVisionProvider(m.provider);
                    }}
                    className={`wk-model-card p-4 cursor-pointer ${selectedVision === m.id ? 'selected' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-white">{m.name}</h4>
                        <span className="text-[10px] uppercase tracking-wider font-mono text-[#a1a1aa]">{m.provider}</span>
                      </div>
                      {selectedVision === m.id && <Check className="w-4 h-4 text-[#f5b400]" />}
                    </div>
                    <p className="text-[11px] text-[#a1a1aa] mb-3 leading-relaxed">{m.description}</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-[#a1a1aa] uppercase tracking-wider">Quality</span>
                        <span className="font-mono text-white">{m.quality_rating}/10</span>
                      </div>
                      <RatingBar value={m.quality_rating} color="#00f5d4" />
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-[#a1a1aa] uppercase tracking-wider">Speed</span>
                        <span className="font-mono text-white">{m.speed_rating}/10</span>
                      </div>
                      <RatingBar value={m.speed_rating} color="#f5b400" />
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-[#a1a1aa] uppercase tracking-wider">Value</span>
                        <span className="font-mono text-white">{m.value_rating}/10</span>
                      </div>
                      <RatingBar value={m.value_rating} color="#6366f1" />
                    </div>
                    <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between">
                      <span className="text-[10px] font-mono text-[#a1a1aa]">${m.price_per_1m_tokens}/1M tokens</span>
                      {m.price_per_1m_tokens === 0 && <Badge className="bg-[#00f5d4]/10 text-[#00f5d4] border-[#00f5d4]/20 text-[9px]">FREE</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Thinking Models */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
              <Cpu className="w-4 h-4 text-[#f5b400]" /> Thinking Models
            </h3>
            <p className="text-xs text-[#a1a1aa] mb-4">Select the LLM for reasoning and wave count analysis</p>
            <ScrollArea className="h-[400px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-4" data-testid="thinking-models-grid">
                {thinkingModels.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`thinking-model-${m.id}`}
                    onClick={() => {
                      setSelectedThinking(m.id);
                      setThinkingProvider(m.provider);
                    }}
                    className={`wk-model-card p-4 cursor-pointer ${selectedThinking === m.id ? 'selected' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-white">{m.name}</h4>
                        <span className="text-[10px] uppercase tracking-wider font-mono text-[#a1a1aa]">{m.provider}</span>
                      </div>
                      {selectedThinking === m.id && <Check className="w-4 h-4 text-[#f5b400]" />}
                    </div>
                    <p className="text-[11px] text-[#a1a1aa] mb-3 leading-relaxed">{m.description}</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-[#a1a1aa] uppercase tracking-wider">Quality</span>
                        <span className="font-mono text-white">{m.quality_rating}/10</span>
                      </div>
                      <RatingBar value={m.quality_rating} color="#00f5d4" />
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-[#a1a1aa] uppercase tracking-wider">Speed</span>
                        <span className="font-mono text-white">{m.speed_rating}/10</span>
                      </div>
                      <RatingBar value={m.speed_rating} color="#f5b400" />
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-[#a1a1aa] uppercase tracking-wider">Value</span>
                        <span className="font-mono text-white">{m.value_rating}/10</span>
                      </div>
                      <RatingBar value={m.value_rating} color="#6366f1" />
                    </div>
                    <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between">
                      <span className="text-[10px] font-mono text-[#a1a1aa]">${m.price_per_1m_tokens}/1M tokens</span>
                      {m.price_per_1m_tokens === 0 && <Badge className="bg-[#00f5d4]/10 text-[#00f5d4] border-[#00f5d4]/20 text-[9px]">FREE</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        {/* API KEYS TAB */}
        <TabsContent value="keys" className="space-y-4">
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader>
              <CardTitle className="text-sm text-white">OpenRouter API Key</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-[#a1a1aa]">Primary key for vision + chat. It can route Gemini/Anthropic/OpenAI models via OpenRouter. Get yours at <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-[#f5b400] hover:underline">openrouter.ai/keys</a></p>
              <form onSubmit={e => e.preventDefault()} className="flex items-center gap-2">
                <Input
                  type="password"
                  data-testid="openrouter-key-input"
                  value={apiKeys.openrouter_key}
                  onChange={e => setApiKeys({ ...apiKeys, openrouter_key: e.target.value })}
                  placeholder={keysConfigured.openrouter ? 'Key configured (enter to replace)' : 'sk-or-v1-...'}
                  className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm font-mono text-sm"
                />
                {keysConfigured.openrouter && <Check className="w-4 h-4 text-[#00f5d4]" />}
              </form>
            </CardContent>
          </Card>

          <Card className="bg-[#14161a] border-white/5">
            <CardHeader>
              <CardTitle className="text-sm text-white">Google AI API Key</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-[#a1a1aa]">Optional native Gemini key. If this is empty, Gemini selections can still run through OpenRouter when configured.</p>
              <form onSubmit={e => e.preventDefault()} className="flex items-center gap-2">
                <Input
                  type="password"
                  data-testid="google-key-input"
                  value={apiKeys.google_ai_key}
                  onChange={e => setApiKeys({ ...apiKeys, google_ai_key: e.target.value })}
                  placeholder={keysConfigured.google ? 'Key configured (enter to replace)' : 'AI...'}
                  className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm font-mono text-sm"
                />
                {keysConfigured.google && <Check className="w-4 h-4 text-[#00f5d4]" />}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RULES TAB */}
        <TabsContent value="rules" className="space-y-4">
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader>
              <CardTitle className="text-sm text-white">Elliott Wave Rules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Allow Diagonals</p>
                  <p className="text-xs text-[#a1a1aa]">Enable leading/ending diagonal patterns</p>
                </div>
                <Switch
                  data-testid="allow-diagonals-switch"
                  checked={ruleSettings.allow_diagonals}
                  onCheckedChange={v => setRuleSettings({ ...ruleSettings, allow_diagonals: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Allow Wave 4 Overlap</p>
                  <p className="text-xs text-[#a1a1aa]">Permit Wave 4 to enter Wave 1 territory</p>
                </div>
                <Switch
                  data-testid="allow-overlap-switch"
                  checked={ruleSettings.allow_wave4_overlap}
                  onCheckedChange={v => setRuleSettings({ ...ruleSettings, allow_wave4_overlap: v })}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-white">ZigZag Sensitivity</p>
                    <p className="text-xs text-[#a1a1aa]">Lower = more pivots detected</p>
                  </div>
                  <span className="font-mono text-sm text-[#f5b400]">{ruleSettings.zigzag_sensitivity}%</span>
                </div>
                <Slider
                  data-testid="zigzag-slider"
                  value={[ruleSettings.zigzag_sensitivity]}
                  onValueChange={v => setRuleSettings({ ...ruleSettings, zigzag_sensitivity: v[0] })}
                  min={1} max={20} step={0.5}
                  className="[&_[role=slider]]:bg-[#f5b400]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-white">Min Wave Size</p>
                    <p className="text-xs text-[#a1a1aa]">Minimum wave size as % of price range</p>
                  </div>
                  <span className="font-mono text-sm text-[#f5b400]">{ruleSettings.min_wave_size_pct}%</span>
                </div>
                <Slider
                  value={[ruleSettings.min_wave_size_pct]}
                  onValueChange={v => setRuleSettings({ ...ruleSettings, min_wave_size_pct: v[0] })}
                  min={0.5} max={10} step={0.5}
                  className="[&_[role=slider]]:bg-[#f5b400]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-white">Fibonacci Tolerance</p>
                    <p className="text-xs text-[#a1a1aa]">How close a ratio must be to expected range</p>
                  </div>
                  <span className="font-mono text-sm text-[#f5b400]">{ruleSettings.fib_tolerance}</span>
                </div>
                <Slider
                  value={[ruleSettings.fib_tolerance * 100]}
                  onValueChange={v => setRuleSettings({ ...ruleSettings, fib_tolerance: v[0] / 100 })}
                  min={1} max={20} step={1}
                  className="[&_[role=slider]]:bg-[#f5b400]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-white">Interpretation Strictness</p>
                    <p className="text-xs text-[#a1a1aa]">Lower values tolerate small wick violations and prefer visual structure</p>
                  </div>
                  <span className="font-mono text-sm text-[#f5b400]">{ruleSettings.interpretation_strictness?.toFixed?.(2) ?? '0.65'}</span>
                </div>
                <Slider
                  value={[(ruleSettings.interpretation_strictness ?? 0.65) * 100]}
                  onValueChange={v => setRuleSettings({ ...ruleSettings, interpretation_strictness: v[0] / 100 })}
                  min={0} max={100} step={1}
                  className="[&_[role=slider]]:bg-[#f5b400]"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Require AI Vision</p>
                  <p className="text-xs text-[#a1a1aa]">Block analysis unless an AI key is configured and the LLM responds.</p>
                </div>
                <Switch
                  data-testid="require-ai-assist-switch"
                  checked={ruleSettings.require_ai_assist ?? true}
                  onCheckedChange={v => setRuleSettings({ ...ruleSettings, require_ai_assist: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Confidence Mode</p>
                  <p className="text-xs text-[#a1a1aa]">Surface only the top-N structures that clear a certainty threshold instead of trying to read everything on the chart.</p>
                </div>
                <Switch
                  checked={ruleSettings.confidence_mode_enabled ?? false}
                  onCheckedChange={v => setRuleSettings({ ...ruleSettings, confidence_mode_enabled: v })}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-white">Confidence Mode Limit</p>
                    <p className="text-xs text-[#a1a1aa]">How many high-certainty structures to surface when Confidence Mode is enabled.</p>
                  </div>
                  <span className="font-mono text-sm text-[#f5b400]">{ruleSettings.confidence_mode_limit}</span>
                </div>
                <Slider
                  value={[ruleSettings.confidence_mode_limit ?? 5]}
                  onValueChange={v => setRuleSettings({ ...ruleSettings, confidence_mode_limit: v[0] })}
                  min={1} max={20} step={1}
                  className="[&_[role=slider]]:bg-[#f5b400]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-white">Confidence Threshold</p>
                    <p className="text-xs text-[#a1a1aa]">Minimum certainty score required before a structure is considered actionable in Confidence Mode.</p>
                  </div>
                  <span className="font-mono text-sm text-[#f5b400]">{ruleSettings.confidence_mode_min_score}</span>
                </div>
                <Slider
                  value={[ruleSettings.confidence_mode_min_score ?? 75]}
                  onValueChange={v => setRuleSettings({ ...ruleSettings, confidence_mode_min_score: v[0] })}
                  min={50} max={95} step={1}
                  className="[&_[role=slider]]:bg-[#f5b400]"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ADMIN TAB */}
        <TabsContent value="admin" className="space-y-4">
          <AdminPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}


